import React, {
  useState, useEffect, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import ArrowFowardIcon from '@material-ui/icons/ArrowForwardIosRounded';
import { Collapse } from 'react-collapse';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { useDispatch, useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { fetchProductCategories, selectAllProductCategories } from 'models/productCategories/categories';
import {
  uncheckCategory,
  checkCategory,
  uncheckAllCategories,
  selectCheckedProductCategoryEntities,
} from 'models/productCategories/selected';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import FilterCheckboxOption from './FilterCheckboxOption';

const MobileHallFilterContainer = styled.div`
  width: 100%;
  background-color: #FFF;
  border-bottom: 0.5px solid #DBDEE2;
  position: relative;
  z-index: 2;

  @media only screen and (min-width: 960px) {
    display: none;
  }
`;

const MobileFilterWrapper = styled.div`
  display: flex;
  justify-content: space-around;
`;

const MobileFilterOption = styled.div`
  padding: 12px 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
  color: #999;

  @media only screen and (max-width: 370px) {
    font-size: 11px;
  }
`;

const MobileFilterSelectContainer = styled.div`
  width: 100%;
  padding: 8px;
  transition: all 0.3s;
`;

const FilterListContainer = styled.div`
  width: 100%;
  max-height: 350px;
  overflow: auto;
`;

const MobileFilterSelect = styled.div`
  font-size: 15px;
  line-height: 18px;
`;

const ArrowContainer = styled.div<{ selected: boolean }>`
  transform: ${({ selected }) => (selected ? 'rotate(-90deg) translateX(-2px)' : 'rotate(90deg) translateX(2px)')};
  display: inline-block;
  transition: inherit;
  padding: 4px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  padding: 20px 16px;
  display: inline-flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const FilterTitleContainer = styled.div<{ singleLevel?: boolean; selected?: boolean; color: string }>`
  cursor: pointer;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: ${({ singleLevel }) => (singleLevel ? 'flex-start' : 'space-between')};
  transition: all 0.3s;
  color:
    ${({ selected, singleLevel, color }) => {
    const selectedColor = color;
    const notSelectedColor = singleLevel ? '#484848' : '#A2AAB3';
    return selected ? selectedColor : notSelectedColor;
  }};
`;

const FilterTitleText = styled.span`
  font-weight: bold;
  font-size: 15px;
  padding: 9px 0;
  display: inline-block;
  text-transform: none;
`;

const StyledArrowFowardIcon = styled(ArrowFowardIcon)`
  font-size: 12px;
`;

const ClearAllButton = ({ children, ...props }: any) => {
  const Custom = withStyles(() => ({
    containedPrimary: {
      textTransform: 'capitalize',
      backgroundColor: '#F0F1F2',
      color: '#000',
      flex: '1 0 auto',
      '&:hover': {
        backgroundColor: '#F0F1F2',
      },
    },
  }))(Button);
  return <Custom {...props as any}>{children as any}</Custom>;
};

const BottomButtonContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const resetCategories = useCallback(() => {
    dispatch(uncheckAllCategories());
  }, [dispatch]);

  return (
    <ButtonContainer>
      <ClearAllButton
        variant="contained"
        color="primary"
        onClick={resetCategories}
      >
        {t('filter.clear_all', 'Clear All')}
      </ClearAllButton>
    </ButtonContainer>
  );
};

const GreyBackground = styled.div`
  background: rgba(0, 0, 0, 0.4);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const FilterOption = ({
  categoryData,
}: any) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);

  const selectedProductCategories = useSelector(selectCheckedProductCategoryEntities);

  const { themeColor } = useSelector((s: any) => s.eventConfigs);

  const onCategorySelect = useCallback((categoryId?: string, checked?: boolean) => {
    dispatch(checked ? checkCategory(categoryId) : uncheckCategory(categoryId));
  }, [dispatch]);

  return (
    <FilterTitleContainer color={themeColor || DEFAULT_THEME_COLOR}>
      <FilterTitleText onClick={() => setOpen((currentState) => !currentState)}>
        <ArrowContainer selected={open}>
          <StyledArrowFowardIcon />
        </ArrowContainer>
        {findAvailableLocale(categoryData.label, locale)}
      </FilterTitleText>
      <Collapse isOpened={open}>
        {categoryData?.subcategories?.map((sub, index) => (
          <FilterCheckboxOption
            key={sub.id}
            label={findAvailableLocale(sub.label, locale)}
            index={index}
            checked={!!selectedProductCategories[sub.id]}
            category={sub.id}
            onClick={onCategorySelect}
          />
        ))}
      </Collapse>
    </FilterTitleContainer>
  );
};

const ProductMobileFilter = () => {
  const { t } = useTranslation();
  const [openTab, setOpenTab] = useState(false);

  const categoriesFetched = useSelector((state) => (state as any).productCategories.categories.fetched as boolean);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!categoriesFetched) {
      dispatch(fetchProductCategories());
    }
  }, [dispatch, categoriesFetched]);

  const productCategories = useSelector(selectAllProductCategories);

  return (
    <>
      {openTab && <GreyBackground />}
      <MobileHallFilterContainer>
        <MobileFilterWrapper>
          <MobileFilterOption onClick={() => setOpenTab((prev) => !prev)}>
            {t('filter.title', 'Filter')}
            <ArrowContainer selected={openTab}>
              <ArrowFowardIcon style={{ fontSize: '12px' }} />
            </ArrowContainer>
          </MobileFilterOption>
        </MobileFilterWrapper>
        <Collapse isOpened={openTab}>
          <MobileFilterSelectContainer>
            <FilterListContainer>
              {productCategories.map((filter) => (
                <MobileFilterSelect key={filter.id}>
                  <FilterOption categoryData={filter} />
                </MobileFilterSelect>
              ))}
            </FilterListContainer>
            <BottomButtonContainer />
          </MobileFilterSelectContainer>
        </Collapse>
      </MobileHallFilterContainer>
    </>
  );
};

export default ProductMobileFilter;
